import type { ApiClient } from "@/modules/core/api/client/apiClient"
import { LoggerService } from "@modules/common/logger/loggerService"
import {
  WebSessionDetailsDtoSchema,
  type UpdateSessionInputDto,
  type WebSessionDetailsDto,
} from "@/modules/sessions/gateway/programInfiniteSessionDto"
import { type ExerciseDto } from "@/modules/sessions/gateway/sessionBlockDto"
import type { TemplateApiGateway } from "@/modules/template/gateway/templateApiGateway"
import type {
  ApplyExerciseTemplateInputDto,
  ApplySessionTemplateInputDto,
  ApplyWeekTemplateInputDto,
  CreateExerciseTemplateInputDto,
  CreateSessionTemplateInputDto,
  CreateWeekTemplateInputDto,
  EditExerciseTemplateInputDto,
  EditTemplateInputDto,
  TemplateDto,
  TemplateElementDto,
} from "@/modules/template/gateway/templateDto"
import {
  EditExerciseTemplateInputDtoSchema,
  TemplateDtoSchema,
  TemplateElementDtoSchema,
  TemplateSessionExerciseDtoSchema,
} from "@/modules/template/gateway/templateDto"
import { TemplateTypeFilter } from "@/modules/template/types/templateType"
import type { Paginated } from "@/modules/utils/paginated"
import { PaginatedDtoSchema } from "@/modules/utils/paginated"
export class TemplateApi implements TemplateApiGateway {
  private templateApiUrl = "/templates"
  private sessionTemplateApiUrl = "/templates/sessions"
  private weekTemplateApiUrl = "/templates/weeks"
  private exerciseTemplateApiUrl = "/templates/exercises"

  constructor(private api: ApiClient) {}

  async getTemplates(
    templateTypeFilter: TemplateTypeFilter,
    searchTerm: string | undefined,
    page: number | undefined,
    pageSize: number | undefined,
  ): Promise<Paginated<TemplateDto>> {
    const resultDto = await this.api.get<Paginated<TemplateDto>>(this.templateApiUrl, {
      params: { templateTypeFilter, search: searchTerm, page, size: pageSize },
    })

    const dataWithParsedContent = {
      ...resultDto.data,
      content: resultDto.data.content.map((item: unknown) => LoggerService.parseApiDto(TemplateDtoSchema, item)),
    }
    return LoggerService.parseApiDto(PaginatedDtoSchema(TemplateDtoSchema), dataWithParsedContent)
  }

  async getTemplate(id: string): Promise<TemplateDto> {
    const resultDto = await this.api.get<TemplateDto>(`${this.templateApiUrl}/${id}`)
    return LoggerService.parseApiDto(TemplateDtoSchema, resultDto.data)
  }

  async getTemplateElement(templateId: string): Promise<TemplateElementDto> {
    const resultDto = await this.api.get(`${this.templateApiUrl}/${templateId}/element`)
    const parsed = TemplateElementDtoSchema.safeParse(resultDto.data)
    if (!parsed.success) {
      console.error("Failed to parse TemplateElementDto:", parsed.error)
      throw parsed.error
    }
    return LoggerService.parseApiDto(TemplateElementDtoSchema, resultDto.data)
  }

  async createExerciseTemplate(exerciseTemplate: CreateExerciseTemplateInputDto): Promise<TemplateDto> {
    const resultDto = await this.api.post<TemplateDto>(this.exerciseTemplateApiUrl, exerciseTemplate)
    return LoggerService.parseApiDto(TemplateDtoSchema, resultDto.data)
  }

  async createSessionTemplate(sessionTemplate: CreateSessionTemplateInputDto): Promise<TemplateDto> {
    const resultDto = await this.api.post<TemplateDto>(this.sessionTemplateApiUrl, sessionTemplate)
    return LoggerService.parseApiDto(TemplateDtoSchema, resultDto.data)
  }

  async createWeekTemplate(weekTemplate: CreateWeekTemplateInputDto): Promise<TemplateDto> {
    const resultDto = await this.api.post<TemplateDto>(this.weekTemplateApiUrl, weekTemplate)
    return LoggerService.parseApiDto(TemplateDtoSchema, resultDto.data)
  }

  async applyExerciseTemplate(
    templateId: string,
    applyExerciseTemplateInput: ApplyExerciseTemplateInputDto,
  ): Promise<ExerciseDto> {
    const resultDto = await this.api.post<ExerciseDto>(
      `${this.exerciseTemplateApiUrl}/${templateId}/apply`,
      applyExerciseTemplateInput,
    )

    const parsed = TemplateSessionExerciseDtoSchema.safeParse(resultDto.data)
    if (!parsed.success) {
      console.error("Failed to parse TemplateElementDto:", parsed.error)
      throw parsed.error
    }
    return resultDto.data
  }

  async applySessionTemplate(
    templateId: string,
    applySessionTemplateInput: ApplySessionTemplateInputDto,
  ): Promise<WebSessionDetailsDto> {
    const resultDto = await this.api.post<WebSessionDetailsDto>(
      `${this.sessionTemplateApiUrl}/${templateId}/apply`,
      applySessionTemplateInput,
    )
    return LoggerService.parseApiDto(WebSessionDetailsDtoSchema, resultDto.data)
  }

  async applyWeekTemplate(
    templateId: string,
    applyWeekTemplateInput: ApplyWeekTemplateInputDto,
  ): Promise<WebSessionDetailsDto[]> {
    const resultDto = await this.api.post<WebSessionDetailsDto[]>(
      `${this.weekTemplateApiUrl}/${templateId}/apply`,
      applyWeekTemplateInput,
    )
    return resultDto.data.map((item: WebSessionDetailsDto) =>
      LoggerService.parseApiDto(WebSessionDetailsDtoSchema, item),
    )
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await this.api.delete(`${this.templateApiUrl}/${templateId}`)
  }

  async deleteSessionTemplate(templateId: string, sessionId: string): Promise<void> {
    await this.api.delete(`${this.sessionTemplateApiUrl}/${templateId}/sessions/${sessionId}`)
  }

  async editTemplate(templateId: string, template: EditTemplateInputDto): Promise<TemplateDto> {
    const resultDto = await this.api.put<TemplateDto>(`${this.templateApiUrl}/${templateId}`, template)
    return LoggerService.parseApiDto(TemplateDtoSchema, resultDto.data)
  }

  async editExerciseTemplate(templateId: string, exerciseInput: EditExerciseTemplateInputDto): Promise<ExerciseDto> {
    const parseResult = EditExerciseTemplateInputDtoSchema.safeParse(exerciseInput)
    if (!parseResult.success) {
      throw new Error("exerciseInput is invalid: " + parseResult.error.message)
    }
    const resultDto = await this.api.put<ExerciseDto>(`${this.exerciseTemplateApiUrl}/${templateId}`, exerciseInput)

    const parsed = TemplateSessionExerciseDtoSchema.safeParse(resultDto.data)
    if (!parsed.success) {
      console.error("Failed to parse TemplateElementDto:", parsed.error)
      throw parsed.error
    }
    return resultDto.data
  }

  async createSessionInTemplate(
    templateId: string,
    sessionInput: UpdateSessionInputDto,
  ): Promise<WebSessionDetailsDto> {
    const resultDto = await this.api.post<WebSessionDetailsDto>(
      `${this.weekTemplateApiUrl}/${templateId}/sessions`,
      sessionInput,
    )
    return LoggerService.parseApiDto(WebSessionDetailsDtoSchema, resultDto.data)
  }

  async editSessionInTemplate(
    templateId: string,
    sessionId: string,
    sessionInput: UpdateSessionInputDto,
  ): Promise<WebSessionDetailsDto> {
    const resultDto = await this.api.put<WebSessionDetailsDto>(
      `${this.weekTemplateApiUrl}/${templateId}/sessions/${sessionId}`,
      sessionInput,
    )
    return LoggerService.parseApiDto(WebSessionDetailsDtoSchema, resultDto.data)
  }
}
